import KoobInputStepper from '../../components/KoobInputStepper';
import { AssignCounter } from './AssignCounter';
import { SubLabel } from './SubLabel';
import type { RoomPax, RoomsSplitFieldProps } from './types';

export type { RoomPax, RoomsSplitFieldProps };

export function RoomsSplitField({
  adultsTotal,
  childrenTotal,
  rooms,
  onRoomsChange,
  limitToTotals = false,
  t,
}: RoomsSplitFieldProps) {
  const sumAdults = rooms.reduce((s, r) => s + r.adults, 0);
  const sumChildren = rooms.reduce((s, r) => s + r.children, 0);
  const adultsMismatch = sumAdults !== adultsTotal;
  const childrenMismatch = sumChildren !== childrenTotal;

  const updateRoom = (i: number, key: 'adults' | 'children', n: number) => {
    onRoomsChange(rooms.map((r, idx) => (idx === i ? { ...r, [key]: n } : r)));
  };

  const addRoom = () => {
    onRoomsChange([...rooms, { adults: 0, children: 0 }]);
  };

  const removeRoom = (i: number) => {
    onRoomsChange(rooms.filter((_, idx) => idx !== i));
  };

  return (
    <div>
      {/* Assignment bar: the counters sit in a header attached to the room list */}
      <div className="flex items-center justify-between gap-4 rounded-t-xl border border-gray-200 bg-gray-50 px-5 py-2">
        <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
          {t('rooms.assigned', { defaultValue: 'Assigned' })}
        </span>
        <p className="flex gap-3 whitespace-nowrap text-sm text-gray-500">
          <AssignCounter
            label={t('rooms.assign', {
              assigned: sumAdults,
              total: adultsTotal,
            })}
            mismatch={adultsMismatch}
            tip={t('rooms.assignMismatch', { total: adultsTotal })}
          />
          {/* KD-3546 shows both counters at all times, children included. */}
          <AssignCounter
            label={t('rooms.assignChildren', {
              assigned: sumChildren,
              total: childrenTotal,
            })}
            mismatch={childrenMismatch}
            tip={t('rooms.assignChildrenMismatch', { total: childrenTotal })}
          />
        </p>
      </div>
      <div className="flex flex-col gap-3 rounded-b-xl border border-t-0 border-gray-200 p-5">
        {rooms.map((room, i) => (
          <div key={i} className="rounded-xl border border-gray-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-semibold text-gray-800">
                {t('rooms.room', { index: i + 1 })}
              </span>
              {rooms.length > 1 && (
                <button
                  type="button"
                  className="border-none bg-transparent text-xs text-gray-400 hover:text-red-500"
                  onClick={() => removeRoom(i)}
                >
                  <i className="fa-light fa-trash" />
                </button>
              )}
            </div>
            <div
              className={`grid gap-4 ${
                childrenTotal > 0 ? 'grid-cols-2' : 'grid-cols-1'
              }`}
            >
              <div>
                <SubLabel>{t('rooms.adults')}</SubLabel>
                <KoobInputStepper
                  value={room.adults}
                  onValueChange={(n) => updateRoom(i, 'adults', n)}
                  min={0}
                  max={
                    limitToTotals
                      ? Math.max(0, adultsTotal - (sumAdults - room.adults))
                      : undefined
                  }
                />
              </div>
              {childrenTotal > 0 && (
                <div>
                  <SubLabel>{t('rooms.children')}</SubLabel>
                  <KoobInputStepper
                    value={room.children}
                    onValueChange={(n) => updateRoom(i, 'children', n)}
                    min={0}
                    max={
                      limitToTotals
                        ? Math.max(
                            0,
                            childrenTotal - (sumChildren - room.children),
                          )
                        : childrenTotal
                    }
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        <button
          type="button"
          className="w-full rounded-xl border border-dashed border-gray-300 bg-transparent py-3 text-sm text-gray-500 hover:border-brand hover:text-brand cursor-pointer"
          onClick={addRoom}
        >
          + {t('rooms.addRoom')}
        </button>
      </div>
    </div>
  );
}
